import React, { useEffect, useState } from 'react';
import { Button, Flex, Form, Input, InputNumber, message, Modal, Select, Switch, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createAgentConfig,
  getAgentConfigDetail,
  updateAgentConfig,
  getSkillBindings,
  batchBindSkills,
  AgentConfigVo,
  RAG_MODE_OPTIONS,
} from '@/api/agent';
import { getModelConfigList, ModelConfigVo } from '@/api/modelConfig';
import { getPromptTemplateList, PromptTemplateVo } from '@/api/prompt';
import { getKnowledgeBaseList, KnowledgeBaseVo } from '@/api/knowledgeBase';
import { getSkillRegistryList, SkillRegistryVo } from '@/api/skill';

export interface EditAgentConfigProp {
  data?: AgentConfigVo;
  isEdit?: boolean;
  updateFn: () => void;
  onCancel: () => void;
}

const EditAgentConfig = ({ data, isEdit, updateFn, onCancel }: EditAgentConfigProp) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modelConfigs, setModelConfigs] = useState<ModelConfigVo[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplateVo[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseVo[]>([]);
  const [skills, setSkills] = useState<SkillRegistryVo[]>([]);

  useEffect(() => {
    // 加载选项数据
    Promise.all([
      getModelConfigList(),
      getPromptTemplateList(),
      getKnowledgeBaseList(),
      getSkillRegistryList(),
    ]).then(([models, prompts, kbs, skillList]) => {
      setModelConfigs(models || []);
      setPromptTemplates(prompts || []);
      setKnowledgeBases(kbs || []);
      setSkills(skillList || []);
    });

    if (isEdit && data?.id) {
      setLoading(true);
      Promise.all([
        getAgentConfigDetail(data.id),
        getSkillBindings(data.id),
      ])
        .then(([res, bindings]) => {
          form.setFieldsValue({
            ...res,
            enableThinking: res.enableThinking === 1,
            enablePlan: res.enablePlan === 1,
            showInCard: res.showInCard !== 0,
            recordHistory: res.recordHistory !== 0,
            recommendedQuestions: res.recommendedQuestions?.slice(0, 10) || [],
            skillIds: bindings?.map(b => b.skillId) || [],
          });
        })
        .finally(() => setLoading(false));
    }
  }, [data?.id, isEdit, form]);

  const onFinish = () => {
    form.validateFields().then(async values => {
      console.log('表单原始值:', values);
      console.log('kbId原始值:', values.kbId, '类型:', typeof values.kbId);
      setLoading(true);
      try {
        const params = {
          ...values,
          id: isEdit ? data?.id : undefined,
          enableThinking: values.enableThinking ? 1 : 0,
          enablePlan: values.enablePlan ? 1 : 0,
          showInCard: values.showInCard ? 1 : 0,
          recordHistory: values.recordHistory ? 1 : 0,
          recommendedQuestions: (values.recommendedQuestions || [])
            .map((question: string) => question.trim())
            .filter(Boolean)
            .slice(0, 10),
          // 显式处理可选字段，确保清空时发送 null 而不是 undefined
          kbId: values.kbId ?? null,
        };
        console.log('提交参数:', params);
        console.log('kbId处理后:', params.kbId, '类型:', typeof params.kbId);
        const skillIds = values.skillIds || [];

        if (isEdit) {
          // 编辑：更新配置 + 更新 Skill 绑定
          await updateAgentConfig(params as any);
          await batchBindSkills({
            agentId: data!.id,
            bindings: skillIds.map(skillId => ({ agentId: data!.id, skillId })),
          });
        } else {
          // 新增：先创建配置获得 ID，再绑定 Skill
          const agentId = await createAgentConfig(params as any);
          if (skillIds.length > 0) {
            await batchBindSkills({
              agentId,
              bindings: skillIds.map(skillId => ({ agentId, skillId })),
            });
          }
        }
        message.success('保存成功');
        onCancel();
        updateFn();
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <Modal
      title={isEdit ? '编辑智能体' : '新增智能体'}
      open={true}
      onCancel={onCancel}
      onOk={onFinish}
      maskClosable={false}
      width={700}
      confirmLoading={loading}
      styles={isEdit ? { body: { maxHeight: 608, overflowY: 'auto' } } : undefined}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            maxIters: 10,
            ragTopK: 5,
            ragThreshold: 0.7,
            showInCard: true,
            recordHistory: true,
            recommendedQuestions: [],
          }}
        >
          <Form.Item name="agentCode" label="智能体编码" rules={[{ required: true, message: '请输入智能体编码' }]}>
            <Input placeholder="智能体唯一标识" disabled={isEdit} />
          </Form.Item>
          <Form.Item name="name" label="智能体名称" rules={[{ required: true, message: '请输入智能体名称' }]}>
            <Input placeholder="请输入智能体名称" />
          </Form.Item>
          <Form.Item name="description" label="智能体描述">
            <Input.TextArea rows={2} placeholder="请输入智能体描述" />
          </Form.Item>
          <Form.Item name="avatarUrl" label="头像 URL">
            <Input placeholder="智能体头像 URL" />
          </Form.Item>
          <Form.Item name="modelConfigId" label="模型配置" rules={[{ required: true, message: '请选择模型配置' }]}>
            <Select
              placeholder="请选择模型配置"
              options={modelConfigs.map(m => ({ label: m.configName, value: m.id }))}
              showSearch
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="promptTemplateId" label="提示词模板" rules={[{ required: true, message: '请选择提示词模板' }]}>
            <Select
              placeholder="请选择提示词模板"
              options={promptTemplates.map(p => ({ label: p.name, value: p.id }))}
              showSearch
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="skillIds" label="绑定 Skill" extra="可多选绑定多个 Skill">
            <Select
              mode="multiple"
              placeholder="请选择要绑定的 Skill"
              options={skills.map(s => ({ label: s.name, value: s.id }))}
              showSearch
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              maxTagCount="responsive"
              allowClear
            />
          </Form.Item>
          <Form.Item name="maxIters" label="最大迭代次数" tooltip="Agent 最大推理轮数">
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="enableThinking" label="启用 Thinking 模式" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="enablePlan" label="启用 Plan 功能" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInCard" label="以卡片形式展示" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="recordHistory" label="记录历史" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="默认推荐问题" extra="支持动态增减，最多 10 个问题">
            <Form.List name="recommendedQuestions">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map(field => (
                    <Flex key={field.key} align="center" gap={8} style={{ marginBottom: 8 }}>
                      <Form.Item
                        {...field}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[{ required: true, whitespace: true, message: '请输入推荐问题' }]}
                      >
                        <Input placeholder="请输入推荐问题" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="删除推荐问题"
                        onClick={() => remove(field.name)}
                      />
                    </Flex>
                  ))}
                  {fields.length < 10 && (
                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>
                      添加推荐问题
                    </Button>
                  )}
                </div>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item name="kbId" label="知识库">
            <Select
              placeholder="请选择知识库（可选）"
              options={knowledgeBases.map(k => ({ label: k.name, value: k.id }))}
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="ragMode" label="RAG 模式">
            <Select options={RAG_MODE_OPTIONS} placeholder="请选择 RAG 模式" />
          </Form.Item>
          <Form.Item name="ragTopK" label="检索数量">
            <InputNumber min={1} max={20} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ragThreshold" label="相关性阈值">
            <InputNumber min={0} max={1} step={0.1} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hookConfig" label="Hook 配置" extra="JSON 格式的 Hook 配置">
            <Input.TextArea rows={3} placeholder='{"hooks": [...]}' />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditAgentConfig;
